import React from 'react';
import { Box, Paper, Typography, CircularProgress, Tooltip, Grid } from '@mui/material';
import { NotificationVolumeByTime, NotificationVolumeByDay } from '../../../types/notification-admin';

interface HeatmapViewProps {
  hourlyData?: NotificationVolumeByTime[];
  dailyData?: NotificationVolumeByDay[];
  loading?: boolean;
}

/**
 * HeatmapView - Heatmap visualization for notification volume by time
 * Shows notification patterns by hour of day and day of week
 */
const HeatmapView: React.FC<HeatmapViewProps> = ({ hourlyData, dailyData, loading = false }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  const getColor = (value: number, max: number): string => {
    const intensity = value / max;
    if (intensity === 0) return '#f5f5f5';
    if (intensity < 0.2) return '#e3f2fd';
    if (intensity < 0.4) return '#90caf9';
    if (intensity < 0.6) return '#42a5f5';
    if (intensity < 0.8) return '#1976d2';
    return '#0d47a1';
  };

  const getTextColor = (value: number, max: number): string => {
    const intensity = value / max;
    return intensity > 0.5 ? '#ffffff' : '#000000';
  };

  const hourLabels = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 || 12;
    const period = i < 12 ? 'AM' : 'PM';
    return `${hour}${period}`;
  });

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Calculate max values for color scaling
  const maxHourly = hourlyData ? Math.max(...hourlyData.map(d => d.count)) : 0;
  const maxDaily = dailyData ? Math.max(...dailyData.map(d => d.count)) : 0;

  return (
    <Grid container spacing={3}>
      {/* Hourly Heatmap */}
      {hourlyData && hourlyData.length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notifications by Hour of Day
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Notification volume distribution across 24 hours
            </Typography>

            <Box sx={{ overflowX: 'auto', pb: 2 }}>
              <Box sx={{ display: 'flex', gap: 0.5, minWidth: 800 }}>
                {hourlyData.map((item) => {
                  const backgroundColor = getColor(item.count, maxHourly);
                  const textColor = getTextColor(item.count, maxHourly);

                  return (
                    <Tooltip
                      key={item.hour}
                      title={`${hourLabels[item.hour]}: ${item.count} notifications`}
                      arrow
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 30,
                          height: 100,
                          backgroundColor,
                          color: textColor,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 2,
                          },
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {hourLabels[item.hour]}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {item.count}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* Legend */}
            <Box display="flex" alignItems="center" gap={1} mt={2}>
              <Typography variant="caption" color="text.secondary">
                Low
              </Typography>
              <Box display="flex" gap={0.5}>
                {[0, 0.2, 0.4, 0.6, 0.8, 1].map((intensity) => (
                  <Box
                    key={intensity}
                    sx={{
                      width: 20,
                      height: 20,
                      backgroundColor: getColor(intensity * maxHourly, maxHourly),
                      borderRadius: 0.5,
                    }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                High
              </Typography>
            </Box>
          </Paper>
        </Grid>
      )}

      {/* Daily Heatmap */}
      {dailyData && dailyData.length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notifications by Day of Week
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Notification volume distribution across the week
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {dailyData.map((item) => {
                const backgroundColor = getColor(item.count, maxDaily);
                const textColor = getTextColor(item.count, maxDaily);

                return (
                  <Tooltip
                    key={item.day}
                    title={`${item.dayName}: ${item.count} notifications`}
                    arrow
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        backgroundColor,
                        color: textColor,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        {dayLabels[item.day]}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {item.count}
                      </Typography>
                      <Typography variant="caption">notifications</Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            {/* Summary */}
            <Box display="flex" justifyContent="space-around" mt={3} pt={2} borderTop="1px solid #e0e0e0">
              <Box textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Total This Week
                </Typography>
                <Typography variant="h6">
                  {dailyData.reduce((sum, item) => sum + item.count, 0).toLocaleString()}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Daily Average
                </Typography>
                <Typography variant="h6">
                  {Math.round(
                    dailyData.reduce((sum, item) => sum + item.count, 0) / dailyData.length
                  ).toLocaleString()}
                </Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Busiest Day
                </Typography>
                <Typography variant="h6">
                  {dailyData.reduce((max, item) => (item.count > max.count ? item : max)).dayName}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      )}

      {!hourlyData && !dailyData && (
        <Grid item xs={12}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No heatmap data available
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default HeatmapView;
